import { useState, useEffect, FormEvent } from 'react';
import { useCatalog, CatalogToolRow } from '../context/CatalogContext';
import { getEmbedUrl } from '../utils/videoEmbed';

const TOKEN_KEY = 'athen-admin-token';

interface Suggestion {
  id: string;
  name: string;
  website: string | null;
  notes: string | null;
  created_at: string;
}

const CATEGORIES: { value: string; label: string }[] = [
  { value: 'scribe', label: 'AI Scribes' },
  { value: 'intake', label: 'Patient Intake' },
  { value: 'chatbot', label: 'Chatbots' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'billing', label: 'Billing & Coding' },
  { value: 'specialty', label: 'Specialty Tools' },
];

const PRICING_TIERS = [
  { value: '', label: 'Not set' },
  { value: 'free', label: 'Free' },
  { value: 'low', label: '$ (low)' },
  { value: 'medium', label: '$$ (medium)' },
  { value: 'enterprise', label: 'Enterprise' },
];

interface FormState {
  name: string;
  website: string;
  category: string;
  subcategory: string;
  description: string;
  hipaaCompliant: boolean;
  pricing: string;
  pricingTier: string;
  bestFor: string;
  keyFeatures: string;
  keywords: string;
  videoUrl: string;
  guideNotes: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  website: '',
  category: 'scribe',
  subcategory: '',
  description: '',
  hipaaCompliant: false,
  pricing: '',
  pricingTier: '',
  bestFor: '',
  keyFeatures: '',
  keywords: '',
  videoUrl: '',
  guideNotes: '',
};

function rowToForm(row: CatalogToolRow): FormState {
  return {
    name: row.name,
    website: row.website,
    category: row.category,
    subcategory: row.subcategory ?? '',
    description: row.description,
    hipaaCompliant: row.hipaa_compliant,
    pricing: row.pricing ?? '',
    pricingTier: row.pricing_tier ?? '',
    bestFor: row.best_for ?? '',
    keyFeatures: (row.key_features ?? []).join(', '),
    keywords: (row.keywords ?? []).join(', '),
    videoUrl: row.video_url ?? '',
    guideNotes: row.guide_notes ?? '',
  };
}

function formToBody(form: FormState) {
  const list = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);
  return {
    name: form.name.trim(),
    website: form.website.trim(),
    category: form.category,
    subcategory: form.subcategory.trim() || undefined,
    description: form.description.trim(),
    hipaaCompliant: form.hipaaCompliant,
    pricing: form.pricing.trim() || undefined,
    pricingTier: form.pricingTier || undefined,
    bestFor: form.bestFor.trim() || undefined,
    keyFeatures: list(form.keyFeatures),
    keywords: list(form.keywords),
    videoUrl: form.videoUrl.trim() || null,
    guideNotes: form.guideNotes.trim() || undefined,
  };
}

export function Admin() {
  const { refreshCatalog } = useCatalog();
  const [token, setToken] = useState<string>(() => localStorage.getItem(TOKEN_KEY) ?? '');
  const [tokenInput, setTokenInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [rows, setRows] = useState<CatalogToolRow[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const fetchRows = async () => {
    try {
      const res = await fetch('/api/v1/tools');
      if (res.ok) setRows((await res.json()) as CatalogToolRow[]);
    } catch {
      // list is non-critical; form still usable
    }
  };

  const fetchSuggestions = async (tok: string) => {
    if (!tok) return;
    try {
      const res = await fetch('/api/v1/tools?suggestions', { headers: { 'x-admin-token': tok } });
      if (res.ok) setSuggestions((await res.json()) as Suggestion[]);
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    fetchSuggestions(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Re-verify a stored token on mount so a stale/invalid one (e.g. typed
  // before validation existed) drops back to the login gate immediately.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch('/api/v1/tools?verify', { headers: { 'x-admin-token': token } })
      .then(res => {
        if (!cancelled && res.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken('');
          setError('Your saved admin token is not valid. Please re-enter it.');
        }
      })
      .catch(() => {
        // Network hiccup: keep the session; writes still verify server-side.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field: keyof FormState) => (value: string | boolean) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleAuthError = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setError('That admin token was rejected. Please re-enter it.');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const url = editingId ? `/api/v1/tools?id=${encodeURIComponent(editingId)}` : '/api/v1/tools';
      const res = await fetch(url, {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
        body: JSON.stringify(formToBody(form)),
      });
      if (res.status === 401) return handleAuthError();
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.error?.message ?? `Save failed (${res.status}).`);
        return;
      }
      setNotice(editingId ? 'Tool updated.' : 'Tool added to the catalog.');
      setForm(EMPTY_FORM);
      setEditingId(null);
      await fetchRows();
      await refreshCatalog();
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: CatalogToolRow) => {
    if (!confirm(`Delete "${row.name}" from the catalog?`)) return;
    setError(null);
    try {
      const res = await fetch(`/api/v1/tools?id=${encodeURIComponent(row.id)}`, {
        method: 'DELETE',
        headers: { 'x-admin-token': token },
      });
      if (res.status === 401) return handleAuthError();
      if (!res.ok && res.status !== 204) {
        setError(`Delete failed (${res.status}).`);
        return;
      }
      if (editingId === row.id) {
        setEditingId(null);
        setForm(EMPTY_FORM);
      }
      await fetchRows();
      await refreshCatalog();
    } catch {
      setError('Network error — please try again.');
    }
  };

  const dismissSuggestion = async (s: Suggestion) => {
    try {
      const res = await fetch(`/api/v1/tools?suggestionId=${encodeURIComponent(s.id)}`, {
        method: 'DELETE',
        headers: { 'x-admin-token': token },
      });
      if (res.status === 401) return handleAuthError();
      setSuggestions(prev => prev.filter(x => x.id !== s.id));
    } catch {
      setError('Network error — please try again.');
    }
  };

  const applySuggestion = (s: Suggestion) => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, name: s.name, website: s.website ?? '', description: s.notes ?? '' });
    setNotice(`Loaded "${s.name}" from suggestions — fill in the rest and save.`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startEdit = (row: CatalogToolRow) => {
    setEditingId(row.id);
    setForm(rowToForm(row));
    setNotice(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Token gate ---------------------------------------------------------
  if (!token) {
    const submitToken = async (e: FormEvent) => {
      e.preventDefault();
      const candidate = tokenInput.trim();
      if (!candidate || verifying) return;
      setVerifying(true);
      setError(null);
      try {
        const res = await fetch('/api/v1/tools?verify', {
          headers: { 'x-admin-token': candidate },
        });
        if (res.status === 204) {
          localStorage.setItem(TOKEN_KEY, candidate);
          setToken(candidate);
          setTokenInput('');
        } else if (res.status === 401) {
          setError('That token is not valid. Check for typos and try again.');
        } else if (res.status === 429) {
          setError('Too many attempts — wait a minute and try again.');
        } else {
          setError('Could not verify the token right now. Please try again.');
        }
      } catch {
        setError('Network error — please try again.');
      } finally {
        setVerifying(false);
      }
    };

    return (
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-2xl font-bold text-neutral-800 font-heading mb-2">Catalog Admin</h1>
        <p className="text-sm text-neutral-500 mb-6">
          Enter the admin token to manage the tool catalog.
        </p>
        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
        <form onSubmit={submitToken} className="flex gap-2">
          <input
            type="password"
            value={tokenInput}
            onChange={e => setTokenInput(e.target.value)}
            placeholder="Admin token"
            className="input-base flex-1"
            autoFocus
          />
          <button type="submit" disabled={verifying} className="btn-primary disabled:opacity-50">
            {verifying ? 'Checking...' : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  const previewEmbed = form.videoUrl.trim() ? getEmbedUrl(form.videoUrl.trim()) : null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800 font-heading">Catalog Admin</h1>
          <p className="text-neutral-500 text-sm mt-1">
            Add or edit tools in the live catalog. Changes appear in Browse All Tools and the AI consultant immediately.
          </p>
        </div>
        <button
          onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(''); }}
          className="text-xs text-neutral-400 hover:text-neutral-600"
        >
          Sign out
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card p-5 mb-8 space-y-4">
        <h2 className="text-sm font-semibold text-neutral-700">
          {editingId ? 'Edit Tool' : 'Add A Tool'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">
              Name <span className="text-red-400">*</span>
            </label>
            <input type="text" value={form.name} onChange={e => set('name')(e.target.value)}
              placeholder="e.g. Abridge" className="input-base" required maxLength={120} />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">
              Website Link <span className="text-red-400">*</span>
            </label>
            <input type="url" value={form.website} onChange={e => set('website')(e.target.value)}
              placeholder="https://..." className="input-base" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">
              Category <span className="text-red-400">*</span>
            </label>
            <select value={form.category} onChange={e => set('category')(e.target.value)} className="input-base">
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Subcategory</label>
            <input type="text" value={form.subcategory} onChange={e => set('subcategory')(e.target.value)}
              placeholder="e.g. Plastic Surgery (for Specialty tools)" className="input-base" maxLength={80} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">
            Description <span className="text-red-400">*</span>
          </label>
          <textarea value={form.description} onChange={e => set('description')(e.target.value)}
            placeholder="One or two sentences on what the tool does and who it's for..."
            rows={2} className="input-base resize-none" required maxLength={2000} />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.hipaaCompliant}
            onChange={e => set('hipaaCompliant')(e.target.checked)}
            className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
          <span className="text-sm font-medium text-neutral-700">HIPAA Compliant (BAA available)</span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Pricing</label>
            <input type="text" value={form.pricing} onChange={e => set('pricing')(e.target.value)}
              placeholder="e.g. Free tier; paid from $99/month" className="input-base" maxLength={300} />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Pricing Tier</label>
            <select value={form.pricingTier} onChange={e => set('pricingTier')(e.target.value)} className="input-base">
              {PRICING_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Best For</label>
          <input type="text" value={form.bestFor} onChange={e => set('bestFor')(e.target.value)}
            placeholder="e.g. Small practices wanting fast clinical notes" className="input-base" maxLength={300} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Key Features (comma-separated)</label>
            <input type="text" value={form.keyFeatures} onChange={e => set('keyFeatures')(e.target.value)}
              placeholder="Real-time notes, EMR export, BAA included" className="input-base" />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Search Keywords (comma-separated)</label>
            <input type="text" value={form.keywords} onChange={e => set('keywords')(e.target.value)}
              placeholder="scribe, documentation, notes" className="input-base" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Guide Video (Loom or YouTube link)</label>
          <input type="url" value={form.videoUrl} onChange={e => set('videoUrl')(e.target.value)}
            placeholder="https://www.loom.com/share/..." className="input-base" />
          {form.videoUrl.trim() && !previewEmbed && (
            <p className="text-xs text-amber-600 mt-1">
              This link can't be embedded (only Loom and YouTube are). It will show as a "Watch video" button instead.
            </p>
          )}
          {previewEmbed && (
            <div className="mt-2 aspect-video rounded-card overflow-hidden border border-neutral-200">
              <iframe src={previewEmbed} title="Guide video preview" className="w-full h-full" allowFullScreen />
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Setup Notes (optional)</label>
          <textarea value={form.guideNotes} onChange={e => set('guideNotes')(e.target.value)}
            placeholder={'Step-by-step notes shown under the video, e.g.\n1. Create an account at ...\n2. ...'}
            rows={5} className="input-base resize-y" maxLength={10000} />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        {notice && <p className="text-xs text-emerald-600">{notice}</p>}

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Tool'}
          </button>
          {editingId && (
            <button type="button" className="btn-secondary"
              onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setError(null); setNotice(null); }}>
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {/* Incoming suggestions ("Suggest a Tool" intake) */}
      {suggestions.length > 0 && (
        <div className="card p-5 mb-8 border-l-2 border-l-primary-400">
          <h2 className="text-sm font-semibold text-neutral-700 mb-1">
            Tool Suggestions From Users ({suggestions.length})
          </h2>
          <p className="text-xs text-neutral-400 mb-4">
            Submitted via "Suggest A Tool" on the browse page. "Use" pre-fills the form above.
          </p>
          <ul className="divide-y divide-neutral-100">
            {suggestions.map(s => (
              <li key={s.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-800 truncate">{s.name}</span>
                    {s.website && (
                      <a href={s.website} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700 truncate">
                        {s.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                    <span className="text-xs text-neutral-400 flex-shrink-0">
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {s.notes && <p className="text-xs text-neutral-500 mt-0.5">{s.notes}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => applySuggestion(s)}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium">Use</button>
                  <button onClick={() => dismissSuggestion(s)}
                    className="text-xs text-neutral-400 hover:text-red-500">Dismiss</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Existing DB tools */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-neutral-700 mb-4">
          Catalog Tools Added Here{rows.length > 0 ? ` (${rows.length})` : ''}
        </h2>
        {rows.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-6">
            No tools added yet. Built-in tools live in code and aren't editable here.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {rows.map(row => (
              <li key={row.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-800 truncate">{row.name}</span>
                    <span className="badge-primary">{CATEGORIES.find(c => c.value === row.category)?.label ?? row.category}</span>
                    {row.hipaa_compliant && <span className="badge-hipaa">HIPAA</span>}
                    {row.video_url && (
                      <span className="text-xs text-neutral-400" title="Has guide video">🎬</span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 truncate mt-0.5">{row.description}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => startEdit(row)}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium">Edit</button>
                  <button onClick={() => handleDelete(row)}
                    className="text-xs text-neutral-400 hover:text-red-500">Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
